import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, Affiliate, Referral, CreateReferralDto, UpdateReferralDto, ReferralType } from '../api/admin';
import { useAdminSSE } from '../hooks/useAdminSSE';
import { Check, X, DollarSign, Clock, CheckCircle, XCircle, Plus, Edit2, Users, Copy, Eye } from 'lucide-react';

type TabType = 'applications' | 'affiliates' | 'referrals';
type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';
type ReferralTypeFilter = 'all' | 'affiliate' | 'advertiser' | 'partner';

export default function AdminAffiliatesPage() {
  const { t } = useTranslation();
  useAdminSSE();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('applications');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [payoutModal, setPayoutModal] = useState<Affiliate | null>(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutReference, setPayoutReference] = useState('');

  // Referrals tab state
  const [referralTypeFilter, setReferralTypeFilter] = useState<ReferralTypeFilter>('all');
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState<Referral | null>(null);
  const [formData, setFormData] = useState<Partial<CreateReferralDto>>({
    type: 'advertiser',
    commissionRate: 0,
    userDiscountEnabled: false,
    stripeCouponMonthly: '',
    stripeCouponYearly: '',
    discountLabel: '',
  });
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyReferralLink = (code: string) => {
    const link = `${window.location.origin}?ref=${code}`;
    navigator.clipboard.writeText(link);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Fetch applications
  const { data: applications = [], isLoading: loadingApps } = useQuery({
    queryKey: ['admin', 'applications', statusFilter],
    queryFn: () => adminApi.listApplications(statusFilter === 'all' ? undefined : statusFilter),
  });

  // Fetch affiliates
  const { data: affiliates = [], isLoading: loadingAffiliates } = useQuery({
    queryKey: ['admin', 'affiliates'],
    queryFn: adminApi.listAffiliates,
    enabled: activeTab === 'affiliates',
  });

  // Fetch referrals
  const { data: referrals = [], isLoading: loadingReferrals } = useQuery({
    queryKey: ['admin', 'referrals', referralTypeFilter],
    queryFn: () => adminApi.listReferrals(
      referralTypeFilter === 'all' ? undefined : { type: referralTypeFilter }
    ),
    enabled: activeTab === 'referrals',
  });

  // Fetch referral funnel data
  const { data: funnelData = [] } = useQuery({
    queryKey: ['admin', 'referral-funnel'],
    queryFn: () => adminApi.getReferralFunnel(),
    enabled: activeTab === 'referrals',
  });

  // Map funnel data by code for quick lookup
  const funnelMap = new Map(funnelData.map((f) => [f.code, f]));

  // Mutations
  const approveMutation = useMutation({
    mutationFn: adminApi.approveApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'applications'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      adminApi.rejectApplication(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'applications'] });
      setRejectingId(null);
      setRejectReason('');
    },
  });

  const payoutMutation = useMutation({
    mutationFn: ({ id, amount, method, reference }: { id: string; amount: number; method: 'bank' | 'paypal'; reference?: string }) =>
      adminApi.recordPayout(id, amount, method, reference),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'affiliates'] });
      setPayoutModal(null);
      setPayoutAmount('');
      setPayoutReference('');
    },
  });

  const createReferralMutation = useMutation({
    mutationFn: (dto: CreateReferralDto) => adminApi.createReferral(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'referrals'] });
      setCreateModal(false);
      resetForm();
    },
  });

  const updateReferralMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateReferralDto }) =>
      adminApi.updateReferral(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'referrals'] });
      setEditModal(null);
      resetForm();
    },
  });

  const resetForm = () => {
    setFormData({
      type: 'advertiser',
      commissionRate: 0,
      userDiscountEnabled: false,
      stripeCouponMonthly: '',
      stripeCouponYearly: '',
      discountLabel: '',
    });
  };

  const openEditModal = (referral: Referral) => {
    setFormData({
      type: referral.type,
      commissionRate: referral.commissionRate,
      userDiscountEnabled: referral.userDiscountEnabled,
      stripeCouponMonthly: referral.stripeCouponMonthly || '',
      stripeCouponYearly: referral.stripeCouponYearly || '',
      discountLabel: referral.discountLabel || '',
      notes: referral.notes,
    });
    setEditModal(referral);
  };

  const handleCreateReferral = () => {
    createReferralMutation.mutate(formData as CreateReferralDto);
  };

  const handleUpdateReferral = () => {
    if (!editModal) return;
    updateReferralMutation.mutate({
      id: editModal._id,
      dto: {
        type: formData.type,
        commissionRate: formData.commissionRate,
        userDiscountEnabled: formData.userDiscountEnabled,
        stripeCouponMonthly: formData.stripeCouponMonthly,
        stripeCouponYearly: formData.stripeCouponYearly,
        discountLabel: formData.discountLabel,
        notes: formData.notes,
      },
    });
  };

  const handleApprove = (id: string) => {
    if (confirm(t('admin.affiliates.confirmApprove'))) {
      approveMutation.mutate(id);
    }
  };

  const handleReject = (id: string) => {
    rejectMutation.mutate({ id, reason: rejectReason });
  };

  const handlePayout = () => {
    if (!payoutModal || !payoutAmount) return;
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) return;

    payoutMutation.mutate({
      id: payoutModal._id,
      amount,
      method: payoutModal.payoutMethod || 'bank',
      reference: payoutReference || undefined,
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-500/20 text-yellow-400 rounded-full flex items-center gap-1"><Clock size={12} /> {t('admin.affiliates.status.pending')}</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs font-medium bg-green-500/20 text-green-400 rounded-full flex items-center gap-1"><CheckCircle size={12} /> {t('admin.affiliates.status.approved')}</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded-full flex items-center gap-1"><XCircle size={12} /> {t('admin.affiliates.status.rejected')}</span>;
      default:
        return null;
    }
  };

  const getReferralTypeBadge = (type: ReferralType) => {
    const styles = {
      affiliate: 'bg-green-500/20 text-green-400',
      advertiser: 'bg-blue-500/20 text-blue-400',
      partner: 'bg-purple-500/20 text-purple-400',
    };
    const labels = {
      affiliate: t('admin.referrals.types.affiliate'),
      advertiser: t('admin.referrals.types.advertiser'),
      partner: t('admin.referrals.types.partner'),
    };
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[type]}`}>{labels[type]}</span>;
  };


  return (
    <div className="py-8">
      <div className="max-w-6xl mx-auto">
        {/* Admin Navigation */}
        <div className="flex gap-4 mb-6">
          <span className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium">
            {t('admin.affiliates.title')}
          </span>
          <Link
            to="/admin/users"
            className="px-4 py-2 bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Users size={18} />
            {t('admin.users.title', 'Users')}
          </Link>
          <Link
            to="/admin/visits"
            className="px-4 py-2 bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Eye size={18} />
            {t('admin.visits.title', 'Visits')}
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('admin.affiliates.title')}
          </h1>
          <p className="text-gray-400">
            {t('admin.affiliates.subtitle')}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'applications'
                ? 'bg-primary-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {t('admin.affiliates.applications')}
            {applications.filter(a => a.status === 'pending').length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-500 text-black rounded-full">
                {applications.filter(a => a.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('affiliates')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'affiliates'
                ? 'bg-primary-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {t('admin.affiliates.activeAffiliates')}
          </button>
          <button
            onClick={() => setActiveTab('referrals')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'referrals'
                ? 'bg-primary-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {t('admin.referrals.title')}
          </button>
        </div>

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <>
            {/* Status Filter */}
            <div className="flex gap-2 mb-4">
              {(['all', 'pending', 'approved', 'rejected'] as StatusFilter[]).map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1 text-sm rounded-lg transition-all ${
                    statusFilter === status
                      ? 'bg-white/20 text-white'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  {t(`admin.affiliates.status.${status}`)}
                </button>
              ))}
            </div>

            {loadingApps ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                {t('admin.affiliates.noApplications')}
              </div>
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <div
                    key={app._id}
                    className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{app.fullName}</h3>
                        <p className="text-sm text-gray-400">
                          {app.userEmail || (typeof app.userId === 'object' ? (app.userId as any)?.email || (app.userId as any)?._id : app.userId)}
                        </p>
                      </div>
                      {getStatusBadge(app.status)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-500">{t('admin.affiliates.fields.country')}:</span>
                        <p className="text-white">{app.country}</p>
                      </div>
                      {app.companyName && (
                        <div>
                          <span className="text-gray-500">{t('admin.affiliates.fields.company')}:</span>
                          <p className="text-white">{app.companyName}</p>
                        </div>
                      )}
                      {app.website && (
                        <div>
                          <span className="text-gray-500">{t('admin.affiliates.fields.website')}:</span>
                          <a href={app.website} target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline block truncate">
                            {app.website}
                          </a>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">{t('admin.affiliates.fields.payout')}:</span>
                        <p className="text-white">
                          {app.payoutMethod === 'bank' ? `Bank: ${app.iban}` : `PayPal: ${app.paypalEmail}`}
                        </p>
                      </div>
                    </div>

                    {app.howWillPromote && (
                      <div className="mb-4">
                        <span className="text-gray-500 text-sm">{t('admin.affiliates.fields.howPromote')}:</span>
                        <p className="text-gray-300 text-sm mt-1">{app.howWillPromote}</p>
                      </div>
                    )}

                    <div className="text-xs text-gray-500 mb-4">
                      {t('admin.affiliates.fields.applied')}: {new Date(app.createdAt).toLocaleDateString()}
                    </div>

                    {app.status === 'pending' && (
                      <div className="flex gap-3">
                        {rejectingId === app._id ? (
                          <div className="flex-1 flex gap-2">
                            <input
                              type="text"
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              placeholder={t('admin.affiliates.rejectPlaceholder')}
                              className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                            />
                            <button
                              onClick={() => handleReject(app._id)}
                              disabled={rejectMutation.isPending}
                              className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                            >
                              {t('admin.affiliates.actions.confirm')}
                            </button>
                            <button
                              onClick={() => { setRejectingId(null); setRejectReason(''); }}
                              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm"
                            >
                              {t('admin.affiliates.actions.cancel')}
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleApprove(app._id)}
                              disabled={approveMutation.isPending}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                            >
                              <Check size={16} /> {t('admin.affiliates.actions.approve')}
                            </button>
                            <button
                              onClick={() => setRejectingId(app._id)}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm font-medium"
                            >
                              <X size={16} /> {t('admin.affiliates.actions.reject')}
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {app.status === 'rejected' && app.rejectionReason && (
                      <div className="mt-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <span className="text-red-400 text-sm">{t('admin.affiliates.fields.reason')}: {app.rejectionReason}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Affiliates Tab */}
        {activeTab === 'affiliates' && (
          <>
            {loadingAffiliates ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
              </div>
            ) : affiliates.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                {t('admin.affiliates.noAffiliates')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-400 border-b border-white/10">
                      <th className="pb-3 pr-4">{t('admin.affiliates.table.affiliate')}</th>
                      <th className="pb-3 pr-4">{t('admin.affiliates.table.code')}</th>
                      <th className="pb-3 pr-4">{t('admin.affiliates.table.conversions')}</th>
                      <th className="pb-3 pr-4">{t('admin.affiliates.table.totalEarned')}</th>
                      <th className="pb-3 pr-4">{t('admin.affiliates.table.paid')}</th>
                      <th className="pb-3 pr-4">{t('admin.affiliates.table.pending')}</th>
                      <th className="pb-3">{t('admin.affiliates.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliates.map((affiliate) => (
                      <tr key={affiliate._id} className="border-b border-white/5">
                        <td className="py-4 pr-4">
                          <div>
                            <p className="text-white font-medium">{affiliate.ownerName || 'N/A'}</p>
                            <p className="text-sm text-gray-400">{affiliate.ownerEmail}</p>
                          </div>
                        </td>
                        <td className="py-4 pr-4">
                          <span className="font-mono text-primary-400">{affiliate.code}</span>
                        </td>
                        <td className="py-4 pr-4 text-white">
                          {affiliate.conversionsCount}
                        </td>
                        <td className="py-4 pr-4 text-green-400 font-medium">
                          €{affiliate.totalEarnings.toFixed(2)}
                        </td>
                        <td className="py-4 pr-4 text-gray-400">
                          €{affiliate.paidEarnings.toFixed(2)}
                        </td>
                        <td className="py-4 pr-4">
                          <span className={affiliate.pendingEarnings >= 50 ? 'text-yellow-400 font-medium' : 'text-gray-400'}>
                            €{affiliate.pendingEarnings.toFixed(2)}
                          </span>
                        </td>
                        <td className="py-4">
                          {affiliate.pendingEarnings > 0 && (
                            <button
                              onClick={() => {
                                setPayoutModal(affiliate);
                                setPayoutAmount(affiliate.pendingEarnings.toFixed(2));
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium"
                            >
                              <DollarSign size={14} /> {t('admin.affiliates.actions.payout')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <>
            {/* Type Filter + Create Button */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-2">
                {(['all', 'affiliate', 'advertiser', 'partner'] as ReferralTypeFilter[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setReferralTypeFilter(type)}
                    className={`px-3 py-1 text-sm rounded-lg transition-all ${
                      referralTypeFilter === type
                        ? 'bg-white/20 text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                  >
                    {type === 'all' ? t('admin.referrals.filterAll') : t(`admin.referrals.types.${type}`)}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg text-sm font-medium"
              >
                <Plus size={16} /> {t('admin.referrals.create')}
              </button>
            </div>

            {loadingReferrals ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
              </div>
            ) : referrals.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                {t('admin.referrals.noReferrals')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-400 border-b border-white/10">
                      <th className="pb-3 pr-4">{t('admin.referrals.table.owner')}</th>
                      <th className="pb-3 pr-4">{t('admin.referrals.table.code')}</th>
                      <th className="pb-3 pr-4">{t('admin.referrals.table.type')}</th>
                      <th className="pb-3 pr-4">{t('admin.referrals.table.commission')}</th>
                      <th className="pb-3 pr-4">{t('admin.referrals.table.discount')}</th>
                      <th className="pb-3 pr-4">{t('admin.referrals.table.visits')}</th>
                      <th className="pb-3 pr-4">{t('admin.referrals.table.leads')}</th>
                      <th className="pb-3 pr-4">{t('admin.referrals.table.conversions')}</th>
                      <th className="pb-3">{t('admin.referrals.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((referral) => (
                      <tr key={referral._id} className={`border-b border-white/5 ${!referral.isActive ? 'opacity-50' : ''}`}>
                        <td className="py-4 pr-4">
                          <div>
                            <p className="text-white font-medium">
                              {referral.ownerId?.username || referral.ownerId?.email || referral.notes || '—'}
                            </p>
                            {referral.ownerId?.email && (
                              <p className="text-sm text-gray-400">{referral.ownerId.email}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 pr-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-primary-400">{referral.code}</span>
                            <button
                              onClick={() => copyReferralLink(referral.code)}
                              className="p-1 hover:bg-white/10 rounded transition-colors"
                              title={t('common.copied')}
                            >
                              {copiedCode === referral.code ? (
                                <Check size={14} className="text-green-400" />
                              ) : (
                                <Copy size={14} className="text-gray-400" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="py-4 pr-4">
                          {getReferralTypeBadge(referral.type)}
                        </td>
                        <td className="py-4 pr-4 text-white">
                          {referral.commissionRate > 0 ? `${(referral.commissionRate * 100).toFixed(0)}%` : '-'}
                        </td>
                        <td className="py-4 pr-4 text-white">
                          {referral.userDiscountEnabled ? (referral.discountLabel || t('admin.referrals.hasDiscount')) : '-'}
                        </td>
                        <td className="py-4 pr-4">
                          <span className="text-white">{funnelMap.get(referral.code)?.visits || 0}</span>
                          <span className="text-gray-500 text-xs ml-1">({funnelMap.get(referral.code)?.uniqueVisitors || 0} uniq)</span>
                        </td>
                        <td className="py-4 pr-4">
                          <span className={`font-medium ${(funnelMap.get(referral.code)?.leads || 0) > 0 ? 'text-blue-400' : 'text-gray-500'}`}>
                            {funnelMap.get(referral.code)?.leads || 0}
                          </span>
                        </td>
                        <td className="py-4 pr-4">
                          <span className={`font-medium ${(funnelMap.get(referral.code)?.conversions || 0) > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                            {funnelMap.get(referral.code)?.conversions || 0}
                          </span>
                          {(funnelMap.get(referral.code)?.leads || 0) > 0 && (
                            <span className="text-gray-500 text-xs ml-1">
                              ({referral.conversions?.length || 0} paid)
                            </span>
                          )}
                        </td>
                        <td className="py-4">
                          <button
                            onClick={() => openEditModal(referral)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm"
                          >
                            <Edit2 size={14} /> {t('admin.referrals.edit')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Create Referral Modal */}
        {createModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-dark-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold text-white mb-4">{t('admin.referrals.createTitle')}</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.code')}</label>
                  <input
                    type="text"
                    value={formData.code || ''}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder={t('admin.referrals.form.codeAutoHint')}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.ownerId')}</label>
                  <input
                    type="text"
                    value={formData.ownerId || ''}
                    onChange={(e) => setFormData({ ...formData, ownerId: e.target.value })}
                    placeholder={t('admin.referrals.form.ownerIdHint')}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.type')}</label>
                  <select
                    value={formData.type || 'advertiser'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as ReferralType })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  >
                    <option value="affiliate">{t('admin.referrals.types.affiliate')}</option>
                    <option value="advertiser">{t('admin.referrals.types.advertiser')}</option>
                    <option value="partner">{t('admin.referrals.types.partner')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.commission')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={(formData.commissionRate || 0) * 100}
                      onChange={(e) => setFormData({ ...formData, commissionRate: parseFloat(e.target.value) / 100 })}
                      className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                      min="0"
                      max="100"
                      step="1"
                    />
                    <span className="text-gray-400">%</span>
                    <span className="text-xs text-gray-500 ml-2">(0% = {t('admin.referrals.noCommission')})</span>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <label className="flex items-center gap-2 text-white mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.userDiscountEnabled || false}
                      onChange={(e) => setFormData({ ...formData, userDiscountEnabled: e.target.checked })}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-primary-600 focus:ring-primary-500"
                    />
                    {t('admin.referrals.form.enableUserDiscount')}
                  </label>

                  {formData.userDiscountEnabled && (
                    <div className="space-y-3 ml-6">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.stripeCouponMonthly')}</label>
                        <input
                          type="text"
                          value={formData.stripeCouponMonthly || ''}
                          onChange={(e) => setFormData({ ...formData, stripeCouponMonthly: e.target.value })}
                          placeholder="REFERRAL_MONTHLY_FREE"
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.stripeCouponYearly')}</label>
                        <input
                          type="text"
                          value={formData.stripeCouponYearly || ''}
                          onChange={(e) => setFormData({ ...formData, stripeCouponYearly: e.target.value })}
                          placeholder="REFERRAL_YEARLY_5EUR"
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.discountLabel')}</label>
                        <input
                          type="text"
                          value={formData.discountLabel || ''}
                          onChange={(e) => setFormData({ ...formData, discountLabel: e.target.value })}
                          placeholder="1 free month"
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('admin.referrals.form.discountLabelHint')}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.notes')}</label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('admin.referrals.form.notesPlaceholder')}
                    rows={2}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleCreateReferral}
                  disabled={createReferralMutation.isPending}
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {createReferralMutation.isPending ? t('admin.referrals.creating') : t('admin.referrals.create')}
                </button>
                <button
                  onClick={() => { setCreateModal(false); resetForm(); }}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg"
                >
                  {t('admin.affiliates.actions.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Referral Modal */}
        {editModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-dark-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold text-white mb-4">{t('admin.referrals.editTitle')}</h3>
              <p className="text-sm text-gray-400 mb-4 font-mono">{editModal.code}</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.type')}</label>
                  <select
                    value={formData.type || 'advertiser'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as ReferralType })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  >
                    <option value="affiliate">{t('admin.referrals.types.affiliate')}</option>
                    <option value="advertiser">{t('admin.referrals.types.advertiser')}</option>
                    <option value="partner">{t('admin.referrals.types.partner')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.commission')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={(formData.commissionRate || 0) * 100}
                      onChange={(e) => setFormData({ ...formData, commissionRate: parseFloat(e.target.value) / 100 })}
                      className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                      min="0"
                      max="100"
                      step="1"
                    />
                    <span className="text-gray-400">%</span>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <label className="flex items-center gap-2 text-white mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.userDiscountEnabled || false}
                      onChange={(e) => setFormData({ ...formData, userDiscountEnabled: e.target.checked })}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-primary-600 focus:ring-primary-500"
                    />
                    {t('admin.referrals.form.enableUserDiscount')}
                  </label>

                  {formData.userDiscountEnabled && (
                    <div className="space-y-3 ml-6">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.stripeCouponMonthly')}</label>
                        <input
                          type="text"
                          value={formData.stripeCouponMonthly || ''}
                          onChange={(e) => setFormData({ ...formData, stripeCouponMonthly: e.target.value })}
                          placeholder="REFERRAL_MONTHLY_FREE"
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.stripeCouponYearly')}</label>
                        <input
                          type="text"
                          value={formData.stripeCouponYearly || ''}
                          onChange={(e) => setFormData({ ...formData, stripeCouponYearly: e.target.value })}
                          placeholder="REFERRAL_YEARLY_5EUR"
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.discountLabel')}</label>
                        <input
                          type="text"
                          value={formData.discountLabel || ''}
                          onChange={(e) => setFormData({ ...formData, discountLabel: e.target.value })}
                          placeholder="1 free month"
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('admin.referrals.form.discountLabelHint')}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.referrals.form.notes')}</label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('admin.referrals.form.notesPlaceholder')}
                    rows={2}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white resize-none"
                  />
                </div>

                <div className="bg-white/5 rounded-lg p-3 text-sm">
                  <p className="text-gray-400">{t('admin.referrals.stats')}:</p>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    <div>
                      <p className="text-xs text-gray-500">{t('admin.referrals.table.conversions')}</p>
                      <p className="text-white font-medium">{editModal.conversions?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('admin.affiliates.table.totalEarned')}</p>
                      <p className="text-green-400 font-medium">€{editModal.totalEarnings.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('admin.affiliates.table.pending')}</p>
                      <p className="text-yellow-400 font-medium">€{editModal.pendingEarnings.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleUpdateReferral}
                  disabled={updateReferralMutation.isPending}
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {updateReferralMutation.isPending ? t('admin.referrals.saving') : t('admin.referrals.save')}
                </button>
                <button
                  onClick={() => { setEditModal(null); resetForm(); }}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg"
                >
                  {t('admin.affiliates.actions.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payout Modal */}
        {payoutModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-dark-900 border border-white/10 rounded-2xl p-6 w-full max-w-md">
              <h3 className="text-xl font-semibold text-white mb-4">{t('admin.affiliates.modal.title')}</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.affiliates.modal.affiliate')}</label>
                  <p className="text-white">{payoutModal.ownerName || payoutModal.ownerEmail}</p>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.affiliates.modal.payoutMethod')}</label>
                  <p className="text-white">
                    {payoutModal.payoutMethod === 'bank'
                      ? `Bank Transfer: ${payoutModal.payoutIban}`
                      : `PayPal: ${payoutModal.payoutPaypalEmail}`}
                  </p>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.affiliates.modal.amount')}</label>
                  <input
                    type="number"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('admin.affiliates.modal.reference')}</label>
                  <input
                    type="text"
                    value={payoutReference}
                    onChange={(e) => setPayoutReference(e.target.value)}
                    placeholder={t('admin.affiliates.modal.referencePlaceholder')}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handlePayout}
                  disabled={payoutMutation.isPending || !payoutAmount}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {payoutMutation.isPending ? t('admin.affiliates.modal.processing') : t('admin.affiliates.modal.confirmPayout')}
                </button>
                <button
                  onClick={() => { setPayoutModal(null); setPayoutAmount(''); setPayoutReference(''); }}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg"
                >
                  {t('admin.affiliates.actions.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
